import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks';
import { fetchMtrBulkDownloadSummary } from '@/integrations/mtrManager/client';
import type { MtrBulkScope } from '@/hooks/useCollectsApiData';
import { useMtrBulkFilter } from '@/hooks/useMtrBulkFilter';
import type { FilterState } from '@/hooks/useFilters';
import { enrichMtrSummary, type MtrBulkDownloadSummaryView } from '@/services/mtrBulkFilter';

/**
 * Resumo do download em massa de MTRs (contagens, lotes e prévia) — alimenta o modal.
 * Duas etapas: o filtro resolvido no agreements e o resumo no mtr-manager; nomes de unidade e
 * material entram aqui, a partir do store. Só dispara quando `enabled` (ex.: modal aberto).
 */
export function useMtrBulkDownloadSummary(
  filters: FilterState,
  enabled: boolean,
  scope: MtrBulkScope = 'historico',
) {
  const establishments = useAppSelector((s) => s.establishmentAccess.establishments);
  const materials = useAppSelector((s) => s.establishmentAccess.materials);
  const resolved = useMtrBulkFilter(filters, scope, enabled);

  const summary = useQuery<MtrBulkDownloadSummaryView>({
    queryKey: ['mtr-bulk-summary', resolved.data?.filter],
    queryFn: async () =>
      enrichMtrSummary(
        await fetchMtrBulkDownloadSummary(resolved.data!.filter),
        establishments,
        materials,
        resolved.data!.totalCollects,
      ),
    enabled: enabled && !!resolved.data,
  });

  return {
    data: summary.data,
    isLoading: resolved.isLoading || summary.isLoading,
    isError: resolved.isError || summary.isError,
    refetch: () => (resolved.isError ? resolved.refetch() : summary.refetch()),
  };
}
